/* eslint-disable global-require */
import seed from 'seed-random'
import { Share } from '@capacitor/share'
import LoadingModel from '@/utils/store/LoadingModel'
import ResourceTag from '@/store/models/collectionItems/ResourceTag'
import User from '@/store/models/user/User'
import Collection from '@/store/models/collections/Collection'
import toast from '@/plugins/toasts'
import i18n from '@/plugins/i18n'
import { ResourceType, NoteColor, UNCROPPED_THUMBNAIL_HEIGHT } from '@/utils/constants'
import { deviceHint, externalReferrer } from '@/utils/beacon-hints'

// Import all placeholder images statically
import placeholder1Thumbnail from '@/assets/img/placeholders/1-thumbnail.webp'
import placeholder2Thumbnail from '@/assets/img/placeholders/2-thumbnail.webp'
import placeholder3Thumbnail from '@/assets/img/placeholders/3-thumbnail.webp'
import placeholder4Thumbnail from '@/assets/img/placeholders/4-thumbnail.webp'
import placeholder5Thumbnail from '@/assets/img/placeholders/5-thumbnail.webp'
import placeholder1Favicon from '@/assets/img/placeholders/1-favicon.webp'
import placeholder2Favicon from '@/assets/img/placeholders/2-favicon.webp'
import placeholder3Favicon from '@/assets/img/placeholders/3-favicon.webp'
import placeholder4Favicon from '@/assets/img/placeholders/4-favicon.webp'
import placeholder5Favicon from '@/assets/img/placeholders/5-favicon.webp'

// Create mapping objects for placeholders
const PLACEHOLDER_THUMBNAILS = {
  1: placeholder1Thumbnail,
  2: placeholder2Thumbnail,
  3: placeholder3Thumbnail,
  4: placeholder4Thumbnail,
  5: placeholder5Thumbnail,
}

const PLACEHOLDER_FAVICONS = {
  1: placeholder1Favicon,
  2: placeholder2Favicon,
  3: placeholder3Favicon,
  4: placeholder4Favicon,
  5: placeholder5Favicon,
}

export const SHOW_COUNT = 25

// Video subtypes under the names people know them by
const FORMAT_LABELS = { quicktime: 'mov', matroska: 'mkv', '3gpp': '3gp' }

export const SHAPES = ['1', '2', '3', '4', '5']
export const COLORS = ['thumbnailsteal', 'thumbnailsmint', 'thumbnailsaquamarine', 'thumbnailsorange', 'thumbnailsblue', 'thumbnailsred']

class CollectionItem extends LoadingModel {
  static entity = 'collectionitems'

  static fields() {
    return {
      ...super.fields(),
      // Common fields
      title: this.string(),
      resourceType: this.string(ResourceType.BOOKMARK), // 'bookmark' or 'note'
      postText: this.string(null).nullable(), // author-written rich-text intro (HTML)
      likes: this.number(0),
      commentsCount: this.number(0),
      hasUnreadComments: this.boolean(false),

      // Bookmark-specific fields
      url: this.string().nullable(),
      description: this.string().nullable(),
      image: this.string().nullable(),
      favicon: this.string().nullable(),
      language: this.string().nullable(),
      readTime: this.number(0).nullable(),
      sld: this.string().nullable(), // second level domain from backend

      // Note-specific fields
      content: this.string().nullable(),
      contentPreview: this.string().nullable(),
      contentPreviewPlainText: this.string().nullable(),
      color: this.string(NoteColor.YELLOW).nullable(),

      // File-specific fields (user uploads). `image` above is the CDN rendition source;
      // the original is only reachable through the api: the signed download `fileUrl`
      // (single reads) or the uploader's public link `fileShareUrl` — never a storage url.
      fileKind: this.string().nullable(),
      fileName: this.string().nullable(),
      fileSize: this.number(0).nullable(),
      fileContentType: this.string().nullable(),
      imageWidth: this.number(0).nullable(),
      imageHeight: this.number(0).nullable(),
      scanStatus: this.string().nullable(),
      fileUrl: this.string().nullable(),
      fileShareUrl: this.string().nullable(),
      // Videos: the streaming copy's state (processing | ready | failed), its length once known,
      // and the signed playlist (single reads only, while ready)
      streamStatus: this.string().nullable(),
      durationSec: this.number(0).nullable(),
      playbackUrl: this.string().nullable(),

      // Flags
      isPrivate: this.boolean(false),
      isFavorite: this.boolean(false),
      isLoading: this.boolean(false),
      isLikedByUser: this.boolean(false),
      searchScore: this.number(0),
      sortOrder: this.number(0),
      deleted: this.boolean(false),
      pinPosition: this.attr(null),
      // { kind, types } or null (no AI label)
      aiLabel: this.attr(null),
      // Unified trash: source-collection context for the deleted-list info row.
      // { id, name, deleted, canRestoreHere, parent } — null for private-collection items.
      sourceCollection: this.attr(null),
      // { role, isOwner } for the current viewer in the owning collection, as resolved by
      // the backend (sub-collections inherit the root role). Only the single-item GET
      // returns it; it is what makes permissions resolvable on a cold deep-link, where
      // the collection and its members are never fetched. See useCollectionItemPermissions.
      viewerMembership: this.attr(null),
      // A moderator blocked the current viewer from commenting on this item: no comment field,
      // no Reply. Only the single-item GETs (member and public) return it.
      viewerBlocked: this.boolean(false),

      // Dates
      createdAt: this.attr(),
      updatedAt: this.attr(),
      deletedAt: this.attr().nullable(),

      // Relationships
      addedById: this.attr(null),
      addedBy: this.belongsTo(User, 'addedById'),
      ownerId: this.attr(null),
      owner: this.belongsTo(Collection, 'ownerId'),
      tagIds: this.attr([]),
      tags: this.hasManyBy(ResourceTag, 'tagIds'),
    }
  }

  // ==================== COMPUTED PROPERTIES ====================

  get rand() {
    return seed(this.id)()
  }

  get colorClasses() {
    return COLORS[Math.floor(this.rand * COLORS.length)]
  }

  get shape() {
    return SHAPES[Math.floor(this.rand * SHAPES.length)]
  }

  get placeholders() {
    const { shape } = this
    return {
      thumbnail: PLACEHOLDER_THUMBNAILS[shape],
      favicon: PLACEHOLDER_FAVICONS[shape],
    }
  }

  // Type checks
  get isBookmark() {
    return this.resourceType === ResourceType.BOOKMARK
  }

  get isNote() {
    return this.resourceType === ResourceType.NOTE
  }

  get isFile() {
    return this.resourceType === ResourceType.FILE
  }

  get isImage() {
    return this.isFile && this.fileKind === 'image'
  }

  get isVideo() {
    return this.isFile && this.fileKind === 'video'
  }

  // "0:07", "2:31", "1:02:03"
  get durationLabel() {
    if (!this.durationSec) return null
    const total = Math.round(this.durationSec)
    const [hours, minutes, seconds] = [Math.floor(total / 3600), Math.floor(total / 60) % 60, total % 60]
    const pad = n => String(n).padStart(2, '0')
    return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
  }

  get isFilePublic() {
    return !!this.fileShareUrl
  }

  // Full-size CDN rendition for the detail view (dynamic param, no dashboard class needed): an
  // image, or a video's thumbnail. A file without one (a DNG without a usable preview) shows the
  // placeholder, like the cards do.
  get detailImageUrl() {
    if (!this.isFile) return null
    if (!this.image) return this.placeholders.thumbnail
    return `${CONFIG.cdnUrl}/${this.image}?width=1600`
  }

  // Natural ratio for the detail image box, clamped to 1:5 … 5:1 so extreme panoramas/strips stay usable
  get imageAspectRatio() {
    if (!this.imageWidth || !this.imageHeight) return 16 / 9
    return Math.min(5, Math.max(0.2, this.imageWidth / this.imageHeight))
  }

  // "jpeg", "png", "svg", "dng", "mov" … from the stored content type
  // ("image/svg+xml" ⇒ "svg", "image/x-adobe-dng" ⇒ "dng", "video/quicktime" ⇒ "mov")
  get fileFormatLabel() {
    const subtype = this.fileContentType?.split('/')[1]?.split('+')[0].split('-').pop()
    return FORMAT_LABELS[subtype] ?? subtype ?? null
  }

  get isCollectionItem() {
    return !this.isPrivate
  }

  // Alias for backwards compatibility with BookmarkActions/BookmarkOptions components
  get isCollectionBookmark() {
    return this.isCollectionItem
  }

  get hasFavicon() {
    return !!this.favicon
  }

  get thumbnailUrl() {
    if (!this.isBookmark && !this.isFile) return this.placeholders.thumbnail
    if (!this.image) return this.placeholders.thumbnail
    return `${CONFIG.cdnUrl}/${this.image}?class=bookmarkthumbnail`
  }

  // An upload's whole image, for a thumbnail box that is not 16:9 (a tier list card
  // keeping the original ratio): the bookmarkthumbnail class crops every image to 16:9
  get uncroppedThumbnailUrl() {
    if (!this.isFile || !this.image) return this.thumbnailUrl
    return `${CONFIG.cdnUrl}/${this.image}?height=${UNCROPPED_THUMBNAIL_HEIGHT}`
  }

  get faviconUrl() {
    if (!this.isBookmark) return this.placeholders.favicon
    if (!this.favicon) return this.placeholders.favicon
    return `${CONFIG.cdnUrl}/${this.favicon}?class=favicon`
  }

  get domain() {
    if (!this.isBookmark || !this.url) return null

    let domain

    // Use backend-provided domain if available
    if (this.sld) {
      domain = this.sld
    } else {
      // Fallback to extracting from URL
      try {
        const addr = new URL(this.url)
        domain = addr.hostname
      } catch {
        return null
      }
    }

    // Strip www. prefix for cleaner display
    return domain.replace(/^www\./i, '')
  }

  get brandedUrl() {
    // Outgoing bookmark URL, returned unchanged — no utm/tracking params are
    // appended so creators' affiliate links stay intact.
    if (!this.isBookmark || !this.url) return null
    return this.url
  }

  get outUrl() {
    // Public-surface clickout counting: the redirect 302s to the raw url unchanged,
    // so affiliate links stay intact — or, for an upload, to the uploader's public link,
    // so a file without one has nothing to open. Only used on public pages.
    const destination = this.isFile ? this.fileShareUrl : this.brandedUrl
    if (!destination) return null
    // Top-level GET, no body — device (iPad desktop-UA fix) and the session's external
    // referrer travel as query params so clickouts land in the same analytics buckets as views
    const params = new URLSearchParams()
    const device = deviceHint()
    const referrer = externalReferrer()
    if (device) params.set('device', device)
    if (referrer) params.set('referrer', referrer)
    const query = params.toString()
    return `${CONFIG.apiUrl}/v1/public/out/${this.id}${query ? `?${query}` : ''}`
  }

  get hasSufficientData() {
    if (this.isNote || this.isFile) {
      return !!this.title
    }
    return !!this.title && !!this.image
  }

  // ==================== METHODS ====================

  static async shareOrCopy({ dialogTitle, title, text, url, toastMessage }) {
    const { value: canShare } = await Share.canShare()
    if (canShare) {
      await Share.share({ dialogTitle, title, text, url })
    } else {
      await navigator.clipboard.writeText(url || text || title)
      toast.info(toastMessage)
    }
  }

  async share({ publicUrl } = {}) {
    const { t } = i18n.global

    if (this.isNote) {
      if (publicUrl) {
        return CollectionItem.shareOrCopy({
          dialogTitle: 'Share note',
          title: this.title,
          url: publicUrl,
          toastMessage: t('common.link_to_note_copied'),
        })
      }
      return CollectionItem.shareOrCopy({
        dialogTitle: 'Share your note',
        title: this.title,
        text: this.content || this.title,
        toastMessage: t('common.note_copied'),
      })
    }

    if (this.isFile) {
      // the original is private unless the uploader made the link public; a public page link wins.
      // A video's original goes as a download: an inline 1 GB stream can't be sought (the player
      // on the public page is the way to watch)
      let originalUrl = this.fileShareUrl
      if (originalUrl && this.isVideo) {
        const download = new URL(originalUrl)
        download.searchParams.set('download', '1')
        originalUrl = download.toString()
      }
      return CollectionItem.shareOrCopy({
        dialogTitle: this.isVideo ? 'Share your video' : 'Share your image',
        title: this.title,
        text: this.title,
        url: publicUrl || originalUrl || undefined,
        toastMessage: t('common.link_copied'),
      })
    }

    return CollectionItem.shareOrCopy({
      dialogTitle: 'Share your bookmark',
      title: this.title,
      text: this.title,
      url: this.brandedUrl,
      toastMessage: t('common.link_copied'),
    })
  }

  isOwner(userId) {
    if (this.isCollectionItem) {
      // if we have collection data
      if (this.owner && this.owner.ownerId === userId) return true
      return this.addedById === userId
    }

    return true
  }
}

export default CollectionItem
